import {
  CalendarBlankIcon,
  EyeIcon,
  IconProps,
  PawPrintIcon,
  PlusCircleIcon,
  SyringeIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { formatDateDDMMYYYY } from '../../../utils/date';
import { cleanDenunciaByTipo } from '../../../utils/siniestro';
import { capitalizeFirstLetter } from '../../../utils/string';
import './SiniestroCard.scss';
import { SiniestroCardProps } from './types';

const badgeIconProps: IconProps = { size: 14, weight: 'bold' };
const infoIconProps: IconProps = { size: 20, weight: 'regular' };

const SiniestroCard: React.FC<SiniestroCardProps> = ({
  denuncia_administrativa,
  isVacuna,
  currentPet,
  fecha_carga,
  documentacionCompleta,
  openDocViewer,
  hasCompletedSVF,
  documentacionExtraCount = 0,
  isDocumentacionExtraLoading,
  onAddDocumentacionExtra,
  onViewDocumentacionExtra,
}) => {
  const motivoText = capitalizeFirstLetter(
    cleanDenunciaByTipo({ denuncia: denuncia_administrativa, isVacuna }),
  );

  return (
    <div className="siniestro-card">
      <div className="siniestro-card__header">
        <h3 className="siniestro-card__title">Detalles del caso médico</h3>
        <div className="siniestro-card__badges">
          {isVacuna && (
            <span className="siniestro-card__badge siniestro-card__badge--vacuna">
              <SyringeIcon {...badgeIconProps} />
              Vacuna
            </span>
          )}
          <span className="siniestro-card__badge">Activo</span>
        </div>
      </div>

      <div className="siniestro-card__content">
        <span className="siniestro-card__motivo-label">Motivo</span>
        <p className="siniestro-card__motivo-value">{motivoText}</p>

        <div className="siniestro-card__info-row">
          <div className="siniestro-card__info-item">
            <div className="siniestro-card__info-icon siniestro-card__info-icon--pet">
              <PawPrintIcon size={20} weight="fill" />
            </div>
            <div className="siniestro-card__info-content">
              <span className="siniestro-card__info-label">Mascota</span>
              <span className="siniestro-card__info-value">
                {capitalizeFirstLetter(currentPet?.nombre)}
              </span>
            </div>
          </div>

          <div className="siniestro-card__info-item">
            <div className="siniestro-card__info-icon siniestro-card__info-icon--date">
              <CalendarBlankIcon {...infoIconProps} />
            </div>
            <div className="siniestro-card__info-content">
              <span className="siniestro-card__info-label">Fecha</span>
              <span className="siniestro-card__info-value">
                {fecha_carga ? formatDateDDMMYYYY(fecha_carga) : '—'}
              </span>
            </div>
          </div>
        </div>

        {documentacionCompleta && (
          <div className="siniestro-card__doc-extra siniestro-card__doc-extra--first">
            <span className="siniestro-card__doc-extra-label">
              Documentación
              <span className="siniestro-card__doc-extra-count">Completada</span>
            </span>
            <div className="siniestro-card__doc-extra-actions">
              <button
                onClick={openDocViewer}
                className="siniestro-card__icon-btn"
                aria-label="Ver documentación"
              >
                <EyeIcon size={20} weight="fill" />
              </button>
            </div>
          </div>
        )}

        {hasCompletedSVF && (
          <div className="siniestro-card__doc-extra">
            <span className="siniestro-card__doc-extra-label">
              Documentación especial
              <span className="siniestro-card__doc-extra-count">
                {isDocumentacionExtraLoading
                  ? 'Cargando...'
                  : documentacionExtraCount > 0 ? documentacionExtraCount : 'Sin archivos'}
              </span>
            </span>
            <div className="siniestro-card__doc-extra-actions">
              {onAddDocumentacionExtra && (
                <button
                  onClick={onAddDocumentacionExtra}
                  className="siniestro-card__icon-btn"
                  aria-label="Agregar documentación especial"
                >
                  <PlusCircleIcon size={20} weight="fill" />
                </button>
              )}

              {onViewDocumentacionExtra && (
                <button
                  onClick={onViewDocumentacionExtra}
                  className="siniestro-card__icon-btn"
                  aria-label="Ver documentación especial"
                  disabled={isDocumentacionExtraLoading || documentacionExtraCount === 0}
                >
                  <EyeIcon size={20} weight="fill" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SiniestroCard);
